import { cn } from '../lib/utils.js';

/**
 * Tiny SVG bar chart. Pure CSS-driven sizing — no external charting
 * library so the SPA bundle stays light. Themed via shadcn tokens
 * (card / border / chart-1) so it matches the rest of the SPA. Rich
 * charts (timeseries, stacked, drilldown) land in Phase B against
 * `recharts` via the shadcn `Chart` wrapper at `components/ui/chart.tsx`;
 * this is the read-only-summary tier.
 */
export interface BarChartDatum {
  label: string;
  value: number;
}

export function BarChart({
  data,
  height = 160,
  emptyLabel = 'No data',
  className,
}: {
  data: ReadonlyArray<BarChartDatum>;
  height?: number;
  emptyLabel?: string;
  className?: string;
}): JSX.Element {
  if (data.length === 0) {
    return (
      <div
        className={cn(
          'rounded-md border border-border bg-card p-6 text-center text-xs text-muted-foreground',
          className,
        )}
      >
        {emptyLabel}
      </div>
    );
  }

  const max = Math.max(...data.map((d) => d.value), 1);
  // Truncate to top 12 to keep the chart readable. Caller can sort.
  const trimmed = data.slice(0, 12);

  return (
    <div className={cn('rounded-md border border-border bg-card p-3', className)}>
      <div role="img" aria-label="Bar chart" className="flex items-end gap-2" style={{ height }}>
        {trimmed.map((d) => {
          const pct = Math.max(2, (d.value / max) * 100);
          return (
            <div
              key={d.label}
              className="flex grow basis-0 flex-col items-center justify-end gap-1"
            >
              <div className="text-[10px] font-mono text-muted-foreground">{d.value}</div>
              <div
                className="w-full rounded-t"
                style={{ height: `${pct}%`, background: 'hsl(var(--chart-1))' }}
              />
              <div
                className="w-full truncate text-center text-[10px] text-foreground/80"
                title={d.label}
              >
                {d.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
